// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).
import { normalizeDocType } from './membership.js';
/** Page ranges for display: [3,4,5,9] -> [{3,5},{9,9}]. Input order and duplicates do not matter. */
export function pageRanges(pages) {
    const sorted = [...new Set(pages)].sort((a, b) => a - b);
    const out = [];
    for (const p of sorted) {
        const last = out[out.length - 1];
        if (last && p === last.end + 1)
            last.end = p;
        else
            out.push({ start: p, end: p });
    }
    return out;
}
const pairKey = (a, b) => (a < b ? `${a}|${b}` : `${b}|${a}`);
/**
 * Req 8.5: a SAME_TYPE flag for every pair of types with equal `normalizeDocType`, then the model's flags that name
 * two different known types and no pair already flagged (deduplicated in either order).
 */
export function mergeFlagsOf(types, modelFlags) {
    const out = [];
    const seen = new Set();
    for (let i = 0; i < types.length; i++) {
        for (let j = i + 1; j < types.length; j++) {
            const a = types[i];
            const b = types[j];
            const type = normalizeDocType(a.documentType);
            if (type !== normalizeDocType(b.documentType))
                continue;
            seen.add(pairKey(a.key, b.key));
            out.push({
                a: a.key,
                b: b.key,
                reason: `Both are '${type}'. A set can have one blueprint per document type.`,
                source: 'SAME_TYPE',
            });
        }
    }
    const keys = new Set(types.map((t) => t.key));
    for (const f of modelFlags) {
        if (f.a === f.b || !keys.has(f.a) || !keys.has(f.b))
            continue;
        const k = pairKey(f.a, f.b);
        if (seen.has(k))
            continue;
        seen.add(k);
        out.push({ a: f.a, b: f.b, reason: f.reason, source: 'MODEL' });
    }
    return out;
}
/** Req 10.2 merge: pages combined, fields unioned by lower-cased name, the first type's definition wins. */
export function mergeTypes(a, b) {
    const names = new Set(a.fields.map((f) => f.name.toLowerCase()));
    const fields = [...a.fields];
    for (const f of b.fields) {
        if (names.has(f.name.toLowerCase()))
            continue;
        names.add(f.name.toLowerCase());
        fields.push(f);
    }
    const merged = {
        ...a,
        pages: [...new Set([...a.pages, ...b.pages])].sort((x, y) => x - y),
        fields,
        fieldsFrom: [...new Set([...a.fieldsFrom, ...b.fieldsFrom])].sort((x, y) => x - y),
        droppedFields: a.droppedFields + b.droppedFields,
    };
    if (a.error === undefined)
        delete merged.error;
    return merged;
}
/** Req 8.7: every page 1..pages is in exactly one of a type, unassigned, or unclassified; no page outside 1..pages. */
export function pageAssignmentErrors(p) {
    const errors = [];
    const owner = new Map();
    const claim = (page, who) => {
        if (!Number.isInteger(page) || page < 1 || page > p.pages) {
            errors.push(`Page ${page} (${who}) is outside 1-${p.pages}.`);
            return;
        }
        const prev = owner.get(page);
        if (prev !== undefined)
            errors.push(`Page ${page} is in both ${prev} and ${who}.`);
        else
            owner.set(page, who);
    };
    for (const t of p.types)
        for (const page of t.pages)
            claim(page, `type ${t.key}`);
    for (const u of p.unassigned)
        claim(u.page, 'unassigned');
    for (const u of p.unclassified)
        claim(u.page, 'unclassified');
    for (let page = 1; page <= p.pages; page++) {
        if (!owner.has(page))
            errors.push(`Page ${page} is not in any type and not listed as unassigned or unclassified.`);
    }
    return errors;
}
//# sourceMappingURL=proposal.js.map